import { type ChangeEvent, PureComponent } from "react";
import { type WithTranslation, withTranslation as withTranslationBase } from "react-i18next";
import { BulkUploadSource, BulkUploadState, type Msg } from "../models";
import { DateFormatType, getPattern } from "../../utils/DateFormatter";
import { userCanSeePriceReference } from "../../Cart/components/CartPage/LineItemStaticParams";
import { isLineLevelCustomerOrderNumberEnabled } from "../../utils/features";
import { Button } from "~/common/components/Button/Button";
import { TextField } from "@mui/material";

interface TextUploadSectionProps extends WithTranslation {
  onTextSubmit: (value: string) => void;
  state: BulkUploadState;
  source: BulkUploadSource;
  messages: Msg[];
}

interface TextUploadSectionState {
  text: string;
}

class TextUploadSection extends PureComponent<TextUploadSectionProps, TextUploadSectionState> {
  state = {
    text: "",
  };

  updateText = (e: ChangeEvent<HTMLTextAreaElement>) => this.setState({ text: e.target.value });
  submit = () => this.props.onTextSubmit(this.state.text);

  componentWillReceiveProps(newProps: TextUploadSectionProps) {
    if (this.props.state !== newProps.state && newProps.state === BulkUploadState.INITIAL) {
      this.setState({ text: "" });
    }
  }

  render() {
    const { state, source, messages, t } = this.props;
    const { text } = this.state;

    const custOrderEnabled = isLineLevelCustomerOrderNumberEnabled();
    const priceReferenceEnabled = userCanSeePriceReference();
    const btnDisabled = !text || state === BulkUploadState.UPLOADING;
    const inputDisabled = state === BulkUploadState.UPLOADING;
    const isInvalid = state === BulkUploadState.ERROR && source === BulkUploadSource.TEXT;
    const colorHelperGreen = !isInvalid && messages.length !== 0;
    const helperMsg =
      messages.length !== 0
        ? messages.map((msg) => msg.label).join(" \n")
        : t("bulkUpload.text.helperText", {
            dateFormat: getPattern(DateFormatType.DATE),
          });

    let placeholderLabel = "";
    if (!custOrderEnabled && !priceReferenceEnabled) {
      placeholderLabel = t("bulkUpload.text.placeholder", {
        dateFormat: getPattern(DateFormatType.DATE),
      });
    } else if (custOrderEnabled && !priceReferenceEnabled) {
      placeholderLabel = t("bulkUpload.text.placeholderCustomerOrder", {
        dateFormat: getPattern(DateFormatType.DATE),
      });
    } else if (!custOrderEnabled && priceReferenceEnabled) {
      placeholderLabel = t("bulkUpload.text.placeholderPriceReference", {
        dateFormat: getPattern(DateFormatType.DATE),
      });
    } else {
      placeholderLabel = t("bulkUpload.text.placeholderCustomerOrderPriceReference", {
        dateFormat: getPattern(DateFormatType.DATE),
      });
    }

    return (
      <div className={"d-flex flex-direction-column w-100 flex-justify-center"}>
        <div className={"scha__bulk-add__text-area-container"}>
          <TextField
            variant={"outlined"}
            multiline
            fullWidth
            placeholder={placeholderLabel}
            data-test-id={"bulk-upload-input-text"}
            onChange={this.updateText}
            disabled={inputDisabled}
            error={isInvalid}
            label={t("bulkUpload.text.label")}
            helperText={helperMsg}
            inputProps={{ sx: { height: "242px !important" } }}
            InputLabelProps={{ shrink: true }}
          />
        </div>
        <Button
          size={"medium"}
          disabled={btnDisabled}
          data-test-id={"bulk-upload-btn-upload-file"}
          onClick={this.submit}
        >
          {t("bulkUpload.text.button")}
        </Button>
      </div>
    );
  }
}

export default withTranslationBase()(TextUploadSection);
